import React, { useEffect, useRef, useState } from 'react';
import './media.css';

/* Renders a real asset into a slot: muted autoplay-loop video for .mp4/.webm,
   otherwise an image. `fit` = 'cover' (default, fills the slot) | 'contain'
   (letterboxed) | 'natural' (frameless, asset's own ratio — for pre-framed
   phone clips).

   Two extras for the phone hero:
   - `mobileSrc`: a still/looping image (e.g. a transparent GIF) shown instead
     of the video on touch/small screens, where alpha video is unreliable.
   - `sound`: when the source carries audio, render a small unmute toggle in the
     bottom-right corner (autoplay must start muted, so the user opts into sound).
*/
export function Media({ src, poster, ratio, alt = '', fit = 'cover', mobileSrc, sound = false, className = '' }) {
  const isVideo = /\.(mp4|webm)$/i.test(src);
  const style = ratio && fit !== 'natural' ? { aspectRatio: ratio.replace('/', ' / ') } : undefined;
  const cls = `pp-media pp-media--${fit} ${className}`.trim();

  // Pick the lightweight image on touch / small viewports (alpha video + autoplay
  // are flaky on mobile). Resolved on mount so SSR/first paint stays safe.
  const [useMobile, setUseMobile] = useState(false);
  useEffect(() => {
    if (!mobileSrc) return;
    const mq = window.matchMedia('(max-width: 768px), (pointer: coarse)');
    const sync = () => setUseMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [mobileSrc]);

  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next) { v.play?.().catch(() => {}); } // unmuting may need an explicit play
  };

  // Mobile fallback image (transparent GIF / still)
  if (mobileSrc && useMobile) {
    return (
      <div className={cls} style={style}>
        <img src={mobileSrc} alt={alt} loading="lazy" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className={cls} style={style}>
        <video ref={videoRef} autoPlay muted loop playsInline poster={poster} preload="metadata" aria-label={alt} role="img">
          <source src={src} type={src.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
        </video>
        {sound && (
          <button
            type="button"
            className="pp-media__sound"
            onClick={toggleSound}
            aria-pressed={!muted}
            aria-label={muted ? 'Unmute' : 'Mute'}
            title={muted ? 'Tap for sound' : 'Mute'}
          >
            {muted ? <SpeakerMuted /> : <SpeakerOn />}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={cls} style={style}>
      <img src={src} alt={alt} loading="lazy" />
    </div>
  );
}

/* ── cute, minimal speaker glyphs ── */
function SpeakerMuted() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9v6h3.5L13 19V5L7.5 9H4Z" fill="currentColor" />
      <path d="M17 9.5l4 5M21 9.5l-4 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function SpeakerOn() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9v6h3.5L13 19V5L7.5 9H4Z" fill="currentColor" />
      <path d="M16.5 9a4 4 0 0 1 0 6M18.8 7a7 7 0 0 1 0 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

export default Media;
